/**
 * AI Insights (formerly "AI Strategy Recommendation") — rendered as its
 * own tab inside `OptionsDashboardPage` (the unified Options Analytics /
 * AI Insights / Strategy Builder workflow). Talks to
 * `/api/v1/options/strategy/{symbol}/*` via `strategyRecommendationService`,
 * a separate feature from `optionsDashboardService`.
 *
 * `onAdjustStrategy` is called (instead of navigating to a standalone
 * route) when the trader clicks "Strategy Builder" on the best-match card
 * (formerly labeled "Adjust Strategy") — the parent page switches to its
 * "Strategy Builder" tab, which already has the same symbol selected.
 *
 * EDUCATIONAL / RESEARCH TOOL ONLY — every string rendered here
 * (explanation bullets, invalidation conditions, market description,
 * educational example) is guaranteed by the backend's
 * `strategy_recommendation.compliance` module to never contain BUY/SELL/
 * ENTER/EXIT/TARGET/STOP LOSS wording. The disclaimer below is always
 * shown, never conditionally hidden.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TuneIcon from "@mui/icons-material/Tune";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import strategyRecommendationService from "@/services/strategyRecommendationService";
import type { StrategyEvaluationRead, StrategyRecommendationCurrentResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

const BADGE_MUI_COLOR: Record<string, "success" | "info" | "warning" | "error" | "default"> = {
  green: "success",
  blue: "info",
  amber: "warning",
  red: "error",
};

function fmtPlain(value?: number | null, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function StarRating({ rating }: { rating?: number | null }) {
  const filled = rating ?? 0;
  return (
    <Stack direction="row" spacing={0} aria-label={`${filled} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) =>
        i <= filled ? (
          <StarIcon key={i} fontSize="small" sx={{ color: "#F5A623" }} />
        ) : (
          <StarBorderIcon key={i} fontSize="small" sx={{ color: "#BDBDBD" }} />
        )
      )}
    </Stack>
  );
}

function TopStrategyRow({ evaluation }: { evaluation: StrategyEvaluationRead }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <StarRating rating={evaluation.star_rating} />
      <Box flexGrow={1}>
        <Typography variant="body1" fontWeight={700}>
          {evaluation.display_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {evaluation.market_thesis}
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight={600}>
        Suitability: {fmtPlain(evaluation.suitability, 0)}%
      </Typography>
      {evaluation.badge && (
        <Chip
          size="small"
          label={evaluation.badge}
          color={BADGE_MUI_COLOR[evaluation.badge_color ?? ""] ?? "default"}
        />
      )}
    </Stack>
  );
}

function BestMatchDetail({
  best,
  summary,
  onAdjustStrategy,
}: {
  best: StrategyEvaluationRead;
  summary: StrategyRecommendationCurrentResponse;
  onAdjustStrategy?: () => void;
}) {
  const example = best.educational_example;
  const [showContext, setShowContext] = useState(false);
  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <AssessmentIcon fontSize="small" color="primary" />
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
              Best Match
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => onAdjustStrategy?.()}
          >
            Strategy Builder
          </Button>
        </Stack>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          {best.display_name}
        </Typography>
        {best.market_description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {best.market_description}
          </Typography>
        )}

        {/* Confidence / Suitability / Risk Profile — the spec's "Confidence,
            Probability of Profit, Risk Meter" row, substituted 1:1 with
            real, already-computed fields (confirmed with the user): no
            numeric Probability of Profit or Risk Meter score exists
            anywhere in this data model, only Confidence %, Suitability %,
            and the existing textual Risk Profile from the educational
            example. Nothing here is a new calculation. */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Strategy Confidence
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {fmtPlain(best.confidence, 0)}%
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Suitability
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {fmtPlain(best.suitability, 0)}%
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Risk Profile
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {example?.risk_profile ?? "—"}
            </Typography>
          </Grid>
        </Grid>

        {/* "Why this strategy?" / "Avoid if" side by side (rather than
            stacked) so the core recommendation fits comfortably on one
            screen without scrolling. */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {best.explanation_bullets.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Why this strategy?
              </Typography>
              <Stack spacing={0.5}>
                {best.explanation_bullets.map((bullet, i) => (
                  <Typography key={i} variant="body2" sx={{ color: "success.main" }}>
                    {bullet}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          )}

          {best.invalidation_conditions.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Avoid if
              </Typography>
              <Stack spacing={0.5}>
                {best.invalidation_conditions.map((condition, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <WarningAmberIcon fontSize="small" sx={{ color: "warning.main", mt: 0.25 }} />
                    <Typography variant="body2" color="text.secondary">
                      {condition}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* Market Context is collapsed by default — Confidence/Suitability/
            Risk Profile + Why/Avoid above already convey the core
            recommendation; the full support/resistance/VIX/PCR context is
            one click away instead of always taking up vertical space. */}
        <Button
          size="small"
          onClick={() => setShowContext((v) => !v)}
          endIcon={<ExpandMoreIcon sx={{ transform: showContext ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />}
          sx={{ mb: showContext ? 1 : 0 }}
        >
          {showContext ? "Hide market context" : "Show market context"}
        </Button>
        <Collapse in={showContext}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
            Market Context
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Expected Market
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {best.market_thesis}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Current Spot
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.spot)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Support
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.support)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Resistance
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.resistance)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Max Pain
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.max_pain)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                India VIX
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.india_vix)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                PCR / OI Score
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fmtPlain(summary.pcr, 2)} / {fmtPlain(summary.oi_score, 1)}
              </Typography>
            </Grid>
            {/* IV/IV-trend Suitability factor backlog item: instrument-
                specific ATM IV rank, distinct from the India VIX tile
                above (see backend `factors.py::factor8_iv_rank`). Only
                shown once >=10 sessions of history exist to compute a
                rank (`summary.iv_rank` is `null` until then). */}
            {summary.iv_rank != null && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Option IV Rank
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {fmtPlain(summary.iv_rank, 0)}%{" "}
                  {summary.atm_iv != null && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({fmtPlain(summary.atm_iv, 1)}% IV)
                    </Typography>
                  )}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Collapse>

        {example && (
          <Accordion disableGutters variant="outlined" sx={{ mb: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Educational Strategy Example</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Illustrative structure only, rounded to nearby strikes — not a live quote and not an
                instruction to trade.
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {example.legs.map((leg, i) => (
                  <Typography key={i} variant="body2" fontFamily="monospace">
                    {leg}
                  </Typography>
                ))}
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Maximum Risk
                  </Typography>
                  <Typography variant="body2">{example.max_risk}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Maximum Reward
                  </Typography>
                  <Typography variant="body2">{example.max_reward}</Typography>
                </Grid>
                {example.strike_width && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Strike Width
                    </Typography>
                    <Typography variant="body2">{example.strike_width}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Breakeven
                  </Typography>
                  <Typography variant="body2">{example.breakeven_formula}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Theta Characteristics
                  </Typography>
                  <Typography variant="body2">{example.theta_characteristics}</Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

// Market-hours auto-refresh (2026-09-25 fix): this section previously
// fetched GET /api/v1/options/strategy/{symbol}/current exactly once per
// mount/symbol-change and never again. Because `OptionsAnalystPage` keeps
// every visited tab mounted for the whole session ("mount once, keep
// alive"), a trader who opened AI Insights early in the day would keep
// looking at that same stale OI Score v2/Local PCR/regime/India VIX bar
// indefinitely, while the Option Indicator tab right next to it polls
// every 60s (`OptionIndicatorPage.tsx`) and the Strategy Builder tab polls
// the same `getCurrent` endpoint every 60s too for its own drift banner
// (`StrategyAdjustmentPage.tsx`'s `DRIFT_POLL_MS`). That mismatch -- not a
// formula/data-source divergence -- is what made AI Insights look "wrong"
// next to Option Indicator: both already read the identical persisted
// `OIScoreSnapshot`/`OIProfessionalAnalytics` row (see
// `build_market_snapshot()` vs `option_indicator_query_service.
// get_dashboard()`, both call `score_repo.get_latest()`/`analytics_repo.
// get_latest_at_or_before()` on the same repositories), so once both
// sides poll at the same 60s cadence they show the same bar. Reusing the
// exact same 60s/market-hours-gated pattern here for consistency rather
// than inventing a new interval for this one tab.
const AI_INSIGHTS_REFRESH_MS = 60_000;
const IST_MARKET_OPEN_MIN = 9 * 60 + 15;
const IST_MARKET_CLOSE_MIN = 15 * 60 + 30;

function istClockMinutesNow(): number {
  const istNow = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return istNow.getUTCHours() * 60 + istNow.getUTCMinutes();
}

function istWeekdayNow(): number {
  const istNow = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return istNow.getUTCDay();
}

function isIstMarketHoursNow(): boolean {
  const weekday = istWeekdayNow();
  const minutes = istClockMinutesNow();
  return weekday >= 1 && weekday <= 5 && minutes >= IST_MARKET_OPEN_MIN && minutes <= IST_MARKET_CLOSE_MIN;
}

export function StrategyRecommendationSection({
  symbol,
  onAdjustStrategy,
}: {
  symbol: string;
  onAdjustStrategy?: () => void;
}) {
  const [data, setData] = useState<StrategyRecommendationCurrentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Distinct from `loading` (which only shows the full-page spinner on the
  // very first load / a symbol change) so a background 60s auto-refresh
  // doesn't blank the whole card out from under a trader who's reading it.
  const [refreshing, setRefreshing] = useState(false);
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;
  // Monotonic request counter -- guards against an OLDER in-flight request
  // (e.g. a background 60s auto-refresh) resolving AFTER a newer one (e.g.
  // a manual Refresh click fired while that auto-refresh was still in
  // flight, or a symbol change) and overwriting the newer snapshot with
  // stale data. Every call to `fetchCurrent` claims the next id; a
  // response is only applied if its id is still the most recent one
  // issued by the time it resolves. This also subsumes the narrower
  // "symbol changed mid-flight" case, since a symbol change triggers its
  // own `fetchCurrent` call and therefore its own new id.
  const latestRequestId = useRef(0);

  const fetchCurrent = useCallback((isBackgroundRefresh: boolean) => {
    const requestId = ++latestRequestId.current;
    if (isBackgroundRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    return strategyRecommendationService
      .getCurrent(symbolRef.current)
      .then((result) => {
        if (latestRequestId.current === requestId) setData(result);
      })
      .catch((err) => {
        if (latestRequestId.current === requestId) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (latestRequestId.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      });
  }, []);

  useEffect(() => {
    fetchCurrent(false);
  }, [symbol, fetchCurrent]);

  // Auto-refresh every 60s, but only during NSE market hours (Mon-Fri,
  // 09:15-15:30 IST) — outside that window the underlying scheduler isn't
  // producing new bars, so polling would just be wasted requests, matching
  // `OptionIndicatorPage.tsx`'s own auto-refresh gate.
  useEffect(() => {
    // `window.setInterval`/`window.clearInterval` (not the bare global) --
    // matches `StrategyAdjustmentPage.tsx`'s own `DRIFT_POLL_MS` interval
    // convention, which avoids an eslint `no-undef` error this project's
    // config raises for the bare `setInterval`/`clearInterval` globals
    // (already relied on Node/DOM lib types elsewhere but not everywhere).
    const id = window.setInterval(() => {
      if (isIstMarketHoursNow()) fetchCurrent(true);
    }, AI_INSIGHTS_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchCurrent]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          AI Insights
        </Typography>
        {data && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Updated {new Date(data.captured_at).toLocaleTimeString()}
              {refreshing ? " · refreshing…" : ""}
            </Typography>
            <Button size="small" onClick={() => fetchCurrent(true)} disabled={refreshing}>
              Refresh
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : data ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Top 3 Ranked Strategies
            </Typography>
            {data.top3.length > 0 ? (
              data.top3.map((evaluation) => <TopStrategyRow key={evaluation.key} evaluation={evaluation} />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not enough data yet to rank strategies for this market.
              </Typography>
            )}

            {data.best_match && (
              <BestMatchDetail best={data.best_match} summary={data} onAdjustStrategy={onAdjustStrategy} />
            )}

            <Divider sx={{ mt: 2, mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              {data.disclaimer}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No strategy recommendation available yet.
        </Typography>
      )}
    </Box>
  );
}

export default StrategyRecommendationSection;
